import type { ReactNode } from 'react';
import { ShieldOff } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@voll-intelligence/ui-kit/ui/popover';

/** A régua ÚNICA da exceção de política: escudo cortado, 16px, sempre em
 *  `destructive`. O escudo de ALERTA (triângulo, `ShieldWarning`) não entra —
 *  é decisão repetida do designer, e o que dividia a marca em quatro glifos e
 *  cinco tamanhos era cada card ter reescrito a sua. */
const GLYPH_SIZE = 'size-4 shrink-0';

/** O alvo: 16px de glifo dentro de 24px de área tocável. O `-m-1` devolve ao
 *  fluxo o espaço que o `p-1` tomou, então nenhuma linha se desloca por causa
 *  do marcador. */
const TARGET_CLASS =
  'text-(--destructive) hover:bg-(--destructive)/10 -m-1 inline-flex w-fit cursor-pointer rounded-full p-1 transition-colors';

/**
 * **O marcador de fora-da-política.** Único portador do fato onde ele aparece,
 * então carrega nome acessível e a explicação — sem palavra ao lado do glifo:
 * o marcador vive em cards de larguras muito diferentes e uma palavra o
 * obrigaria a mudar de lugar entre elas.
 *
 * Duas portas para a explicação, mesma marca:
 *
 * - sem `onOpenDetails`, o clique abre um popover com o motivo — popover e não
 *   tooltip de hover porque estes cards moram em WebView, onde hover não
 *   existe. O hover é atalho de mouse, não o único caminho.
 * - com `onOpenDetails`, o clique entrega ao chamador, que tem uma explicação
 *   MAIOR para mostrar (o drawer de regras do voo). Aí o popover sairia na
 *   frente do drawer dizendo menos.
 */
export function OutOfPolicyMark({
  label,
  detail,
  reasons,
  hint,
  onOpenDetails,
}: {
  readonly label: string;
  /** O motivo concreto ("Acima de R$ 520,00"), quando o dado chega. Vira o
   *  título do popover no lugar do rótulo genérico. */
  readonly detail?: string;
  /** Todas as regras quebradas, uma por linha, abaixo do título — para o card
   *  cuja exceção tem mais de um motivo. */
  readonly reasons?: readonly string[];
  readonly hint?: string;
  readonly onOpenDetails?: () => void;
}): ReactNode {
  if (onOpenDetails) {
    return (
      <button
        type="button"
        aria-label={label}
        title={detail ?? label}
        className={TARGET_CLASS}
        onClick={(event) => {
          // O marcador mora dentro de linhas que são elas mesmas alvo de
          // clique: sem parar aqui, pedir a explicação escolhia a oferta.
          event.stopPropagation();
          onOpenDetails();
        }}
      >
        <ShieldOff className={GLYPH_SIZE} aria-hidden />
      </button>
    );
  }
  return (
    <Popover>
      <PopoverTrigger
        openOnHover
        delay={120}
        render={
          <button
            type="button"
            aria-label={label}
            className={TARGET_CLASS}
            // `stopPropagation` nos DOIS caminhos, não só no do `onOpenDetails`:
            // a linha de ônibus é ela mesma alvo de clique (`<li onClick>`), e
            // sem isto pedir a explicação escolhia a viagem. O `mergeProps` do
            // primitivo mantém o toggle do popover — só o borbulho morre.
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <ShieldOff className={GLYPH_SIZE} aria-hidden />
          </button>
        }
      />
      <PopoverContent className="text-caption grid w-60 gap-1">
        <span className="font-semibold">{detail ?? label}</span>
        {reasons && reasons.length > 0 ? (
          <ul className="grid list-disc gap-0.5 pl-4">
            {reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        ) : null}
        {hint ? <span className="text-muted-foreground">{hint}</span> : null}
      </PopoverContent>
    </Popover>
  );
}

/**
 * O mesmo glifo, sem interação — para quando o marcador já vive DENTRO de um
 * alvo de clique. Botão dentro de botão é HTML inválido, e o card de oferta
 * inteiro é um `<button>`: ali a explicação chega um toque adiante, no painel
 * de quartos, e o que o card deve garantir é o nome acessível.
 *
 * O `title` cobre o hover de mouse na lista, onde o card-botão impede o
 * popover — `label` é o padrão, mas um chamador com explicação mais rica (o
 * rodapé do checkout, que já sabia o motivo concreto) pode sobrescrevê-lo sem
 * duplicar o `title` num span embrulhando este glifo: DUAS fontes de tooltip
 * aninhadas fariam o navegador mostrar sempre a de dentro, calando a de fora.
 * O nome acessível (`aria-label`) continua fixo em `label`.
 *
 * ponytail: existe só por causa do aninhamento. Se o card de oferta virar
 * `div` + alvo interno, este export morre e o `OutOfPolicyMark` assume.
 */
export function OutOfPolicyGlyph({
  label,
  title,
}: {
  readonly label: string;
  /** Sobrescreve o texto do `title` sem mexer no nome acessível. */
  readonly title?: string;
}): ReactNode {
  return (
    <span title={title ?? label} className="inline-flex">
      <ShieldOff className={`text-(--destructive) ${GLYPH_SIZE}`} role="img" aria-label={label} />
    </span>
  );
}
